import type { Change } from "../protocol.ts";
import { ident, type Row, type SqlExecutor, type SqlValue } from "./db.ts";

export interface TableInfo {
  name: string;
  columns: Set<string>;
  pk: string;
}

export async function tableInfo(db: SqlExecutor, table: string): Promise<TableInfo | null> {
  const cols = await db.query<{ name: string; pk: number }>(`PRAGMA table_info(${ident(table)})`);
  if (!cols.length) return null;
  const pk = cols.find((c) => c.pk === 1)?.name;
  if (!pk) return null;
  return { name: table, columns: new Set(cols.map((c) => c.name)), pk };
}

const snake = (k: string) => k.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());

/** JSON → SQLite value: booleans become 0/1, arrays and objects become JSON text. */
export function toSqlValue(v: unknown): SqlValue | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "number" || typeof v === "string" || typeof v === "bigint") return v;
  if (v instanceof Uint8Array) return v;
  return JSON.stringify(v);
}

/**
 * Turns a `put` change into a local row. Accepts SQL column names (`label_ids`) or
 * Drizzle field names (`labelIds`), drops columns the local table doesn't have, and
 * fills the primary key and version from the change if the row omits them.
 */
export function rowFromChange(info: TableInfo, c: Extract<Change, { op: "put" }>): Row {
  const row: Row = {};
  for (const [k, v] of Object.entries(c.row)) {
    const col = info.columns.has(k) ? k : snake(k);
    if (!info.columns.has(col)) continue;
    const sv = toSqlValue(v);
    if (sv !== undefined) row[col] = sv;
  }
  row[info.pk] = c.key;
  if (info.columns.has("version") && row.version === undefined) row.version = c.v;
  return row;
}

/** Upsert that keeps the rowid stable (the contentless FTS index is keyed by rowid). */
export async function writeRow(db: SqlExecutor, info: TableInfo, row: Row): Promise<void> {
  const cols = Object.keys(row).filter((c) => info.columns.has(c));
  const updates = cols.filter((c) => c !== info.pk).map((c) => `${ident(c)} = excluded.${ident(c)}`);
  await db.exec(
    `INSERT INTO ${ident(info.name)} (${cols.map(ident).join(", ")}) VALUES (${cols.map(() => "?").join(", ")})` +
      (updates.length ? ` ON CONFLICT(${ident(info.pk)}) DO UPDATE SET ${updates.join(", ")}` : ` ON CONFLICT DO NOTHING`),
    cols.map((c) => row[c]!),
  );
}

export async function readRow(db: SqlExecutor, info: TableInfo, key: string): Promise<Row | null> {
  return (await db.query(`SELECT * FROM ${ident(info.name)} WHERE ${ident(info.pk)} = ?`, [key]))[0] ?? null;
}

export async function deleteRow(db: SqlExecutor, info: TableInfo, key: string): Promise<void> {
  await db.exec(`DELETE FROM ${ident(info.name)} WHERE ${ident(info.pk)} = ?`, [key]);
}

export function jsonArray(v: SqlValue | undefined): string[] {
  if (typeof v !== "string") return [];
  try {
    const a: unknown = JSON.parse(v);
    return Array.isArray(a) ? a.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
